import React from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { FormSubmitEvent } from 'ui/base/types';
import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';

import { PlainButton } from 'ui/public/button';
import { LabeledInput, TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { AvatarInput } from 'ui/standard/input/avatar_input/avatar_input';
import { CountryInput } from 'ui/standard/input/country_input/country_input';

import { BasePath, pagePathToPathString } from 'services/base/page_path';
import { ResultKind } from 'services/base/types';

import { IAccountsDomain, User } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import BaseStyles from './edit_settings.module.css';
import Messages from './edit_settings_messages';

import { EditSettingsStore } from './edit_settings_store';

type EditSettingsStyles = {
  headerContainer: string;
  profilePhotoContainer: string;
  avatar: string;
  fillOutContianer: string;
  changePassword: string;
  save: string;
  row: string;
  prefix: string;
  link: string;
  notVerifiedMessage: string;
};

const themedStyles: ThemedStyles<EditSettingsStyles> = {
  [Theme.DAYLIGHT]: {
    headerContainer: BaseStyles.headerContainer,
    profilePhotoContainer: BaseStyles.profilePhotoContainer,
    avatar: BaseStyles.avatar,
    fillOutContianer: BaseStyles.fillOutContianer,
    changePassword: BaseStyles.changePassword,
    save: BaseStyles.daylightSave,
    row: BaseStyles.mobileContainer,
    prefix: BaseStyles.daylightPrefix,
    link: BaseStyles.daylightLink,
    notVerifiedMessage: BaseStyles.daylightNotVerifiedMessage,
  },
  [Theme.MIDNIGHT]: {
    headerContainer: BaseStyles.headerContainer,
    profilePhotoContainer: BaseStyles.profilePhotoContainer,
    avatar: BaseStyles.avatar,
    fillOutContianer: BaseStyles.fillOutContianer,
    changePassword: BaseStyles.changePassword,
    save: BaseStyles.midnightSave,
    row: BaseStyles.mobileContainer,
    prefix: BaseStyles.midnightPrefix,
    link: BaseStyles.midnightLink,
    notVerifiedMessage: BaseStyles.midnightNotVerifiedMessage,
  },
};

type BaseEditSettingsProps = {
  accounts: IAccountsDomain;
  user: User;
};

export const BaseEditSettings = observer(({ accounts, user }: BaseEditSettingsProps) => {
  const history = useHistory();

  const [store] = React.useState(() => new EditSettingsStore({
    email: user.email,
    country: user.country,
    mobileNumber: user.mobileNumber,
    mobileNumberPrefix: user.mobileNumberPrefix,
    avatarCode: user.avatarCode,
  }));

  const handleEdit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleEditSettings(accounts));
    if (result.kind === ResultKind.OK) {
      if (accounts.user?.unverifiedEmail == null || accounts.user.unverifiedEmail.length === 0) {
        history.goBack();
      } else {
        history.replace(pagePathToPathString({ kind: BasePath.EMAIL_VERIFICATION }));
      }
    }
  }, [accounts, history, store]);

  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <form onSubmit={handleEdit}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <PlainButton type='submit'
            busy={store.busy} reserveBusyIconArea={true}
            className={styles.save}>{Messages.save()}</PlainButton>
        </div>
        <div className={styles.profilePhotoContainer}>
          <AvatarInput store={store.avatar}/>
        </div>
        <div className={styles.fillOutContianer}>
          <LabeledInput label={Messages.emailAddress()}>
            <TextInput store={store.email} id='email-address' name='email-address'/>
            <ErrorMessage message={store.emailError}/>
            {accounts.user?.unverifiedEmail == null || accounts.user.unverifiedEmail.length === 0 ? null :
              <Link path={{ kind: BasePath.EMAIL_VERIFICATION }} className={styles.link}>
                <div className={styles.notVerifiedMessage}>
                  {Messages.verifyYourEmailAdress()} {accounts.user?.unverifiedEmail}
                </div>
              </Link>
            }
          </LabeledInput>
          <LabeledInput label={Messages.country()}>
            <CountryInput store={store.country} placeholder='Country'/>
          </LabeledInput>
          <ErrorMessage message={store.countryError}/>
          <LabeledInput label={Messages.mobileNumber()}>
            <div className={styles.row}>
              <div className={styles.prefix}>{Messages.mobilePrefix(store.country.prefix.value)}</div>
              <TextInput store={store.mobileNumber} id='mobile' name='mobile'/>
            </div>
          </LabeledInput>
          <ErrorMessage message={store.mobileNumberError}/>
        </div>
        <div className={styles.changePassword}>
          <Link path={{ kind: BasePath.SETTINGS_PASSWORD_CHANGE }} className={styles.link}>
            {Messages.changePassword()}
          </Link>
        </div>
        <div className={styles.changePassword}>
          <Link path={{ kind: BasePath.SETTINGS_ACCOUNT_DELETE }} className={styles.link}>
            {Messages.deleteAccount()}
          </Link>
        </div>
      </form>
    </StandardPage>
  );
});

export const EditSettings = observer(() => {
  const accounts = useAccountsDomain();

  const user = accounts.user;
  if (!user) return <Redirect />;

  return (
    <BaseEditSettings
      accounts={accounts}
      user={user}
    />
  );
});
